/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {SkeletonLoader} from '@ion/dls/src/components/loading/SkeletonLoader';
import {dlsColor} from '@ion/dls/src/theme/theme';

const LOADER_HEIGHT = 16;
const LOADER_WIDTH = 200;

const useStyles = makeStyles(() => ({
  loaderContainer : {
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'flex-start',
    padding        : 10,
    gap            : 10
  },
  loaderWrapper : {
    padding        : '4px 10px 16px',
    borderBottom   : `1px solid ${dlsColor('grey5')}`,
    '&:last-child' : {
      borderBottom : 'none'
    }
  }
}));

/**
 * @DynamicLookupOptionsLoader
 *
 * The DynamicLookupOptionsLoader component provides a placeholder representation for
 * dynamic lookup options that are yet to load. It helps in enhancing the perceived
 * performance and provides better user experience during content loading times.
 *
 * ```typescript
 * import {DynamicLookupOptionsLoader} from '@ion/features/src/search/DynamicLookupOptionsLoader';
 *
 * <SomeComponent>
 *   {loading ? (<DynamicLookupOptionsLoader /> : <SomeOtherComponent />)}
 * </SomeComponent>
 *
 * ```
 */
const DynamicLookupOptionsLoader = () => {
  const classes = useStyles();

  return (
    <div className={classes.loaderContainer}>
      <div className={classes.loaderWrapper}>
        <SkeletonLoader width={`${LOADER_WIDTH + 20}px`} height={`${LOADER_HEIGHT}px`} />
      </div>
      <div className={classes.loaderWrapper}>
        <SkeletonLoader width={`${LOADER_WIDTH + 60}px`} height={`${LOADER_HEIGHT}px`} />
      </div>
      <div className={classes.loaderWrapper}>
        <SkeletonLoader width={`${LOADER_WIDTH - 20}px`} height={`${LOADER_HEIGHT}px`} />
      </div>
      <div className={classes.loaderWrapper}>
        <SkeletonLoader width={`${LOADER_WIDTH + 20}px`} height={`${LOADER_HEIGHT}px`} />
      </div>
    </div>
  );
};

export default DynamicLookupOptionsLoader;
